import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('es');
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    // Load token and user
    const token = localStorage.getItem('trebol_token');
    const savedUser = localStorage.getItem('trebol_user');

    if (token && savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
        setLanguage(parsedUser.language || 'es');
      } catch (e) {
        localStorage.removeItem('trebol_token');
        localStorage.removeItem('trebol_user');
      }
    }

    // Load theme preference
    const savedTheme = localStorage.getItem('trebol_theme');
    if (savedTheme) {
      setTheme(savedTheme);
      applyTheme(savedTheme);
    } else {
      // Default to light as requested
      const defaultTheme = 'light';
      setTheme(defaultTheme);
      applyTheme(defaultTheme);
    }

    setLoading(false);
  }, []);

  const applyTheme = (newTheme) => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(newTheme);

    // Update meta theme-color for PWA
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute('content', newTheme === 'dark' ? '#0a0a0a' : '#064E3B');
    }
  };

  const changeTheme = (newTheme) => {
    setTheme(newTheme);
    localStorage.setItem('trebol_theme', newTheme);
    applyTheme(newTheme);
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    changeTheme(newTheme);
  };

  const login = async (email, password, totpCode = null) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    const response = await authAPI.login(payload);
    const { access_token, user: userData } = response.data;

    localStorage.setItem('trebol_token', access_token);
    localStorage.setItem('trebol_user', JSON.stringify(userData));
    setUser(userData);
    setLanguage(userData.language || 'es');

    return userData;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    // New flow: backend returns pending_verification (no token yet)
    // Token is returned after email verification in /auth/verify-email
    return response;
  };

  const logout = () => {
    localStorage.removeItem('trebol_token');
    localStorage.removeItem('trebol_user');
    setUser(null);
  };

  const loginWithToken = (access_token, userData) => {
    // Called after email verification — activates session with returned token
    localStorage.setItem('trebol_token', access_token);
    localStorage.setItem('trebol_user', JSON.stringify(userData));
    setUser(userData);
    setLanguage(userData.language || 'es');
  };

  const updateUser = (newData) => {
    const updated = { ...user, ...newData };
    localStorage.setItem('trebol_user', JSON.stringify(updated));
    setUser(updated);
    if (newData.language) setLanguage(newData.language);
  };

  const changeLanguage = (newLang) => {
    setLanguage(newLang);
    if (user) {
      updateUser({ language: newLang });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      language,
      theme,
      login,
      register,
      logout,
      loginWithToken,
      updateUser,
      changeLanguage,
      changeTheme,
      toggleTheme,
      isAuthenticated: !!user,
      isCeo: user?.role === 'ceo',
      needsOnboarding: user && !user.onboarding_completed,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
